import { useState, useCallback, useRef } from 'react';
import axios from "axios";
import { useAuth0 } from "../react-auth0-spa";
import { useAppContext } from "../context";
import config from "../api_config.json";

// GraphQL queries
const editorCanvasesQuery = `
  query EditorCanvasesQuery($first: Int!, $skip: Int, $where: Editor_canvasWhereInput, $order: [Editor_canvasOrder!]) {
    findEditorCanvas(
      first: $first,
      skip: $skip,
      where: $where,
      order: $order
    ) {
      count
      edges {
        node {
          objectId
          createdAt
          updatedAt
          status
          metadata
          org_pointer {
            objectId
            nome
          }
          editor_canvas_pages {
            edges {
              node {
                objectId
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
  }`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const editorTemplatesQuery = `
  query EditorTemplatesQuery($first: Int!, $skip: Int, $where: Editor_templatesWhereInput, $order: [Editor_templatesOrder!]) {
    findEditorTemplates(
      first: $first,
      skip: $skip,
      where: $where,
      order: $order
    ) {
      count
      edges {
        node {
          objectId
          createdAt
          updatedAt
          name
          metadata
          editor_templates_pages {
            edges {
              node {
                objectId
                preview {
                  url
                }
              }
            }
          }
        }
      }
    }
  }`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const editorImagesQuery = `
  query EditorImagesQuery($first: Int!, $skip: Int, $where: Editor_images_defaultWhereInput, $order: [Editor_images_defaultOrder!]) {
    editor_images_defaults(
      first: $first,
      skip: $skip,
      where: $where,
      order: $order
    ) {
      count
      edges {
        node {
          objectId
          createdAt
          updatedAt
          file {
            url
          }
          custom_preview {
            url
          }
        }
      }
    }
  }`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

const editorBackgroundsQuery = `
  query EditorBackgroundsQuery($first: Int!, $skip: Int, $where: Editor_backgroundsWhereInput, $order: [Editor_backgroundsOrder!]) {
    findEditorBackgrounds(
      first: $first,
      skip: $skip,
      where: $where,
      order: $order
    ) {
      count
      edges {
        node {
          objectId
          createdAt
          updatedAt
          file {
            url
          }
          preview {
            url
          }
        }
      }
    }
  }`.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

/**
 * Hook for managing Giotto data with GraphQL and infinite scroll
 * Works for both single organization and global views
 */
export const useGiottoData = (orgId = null) => {
  const [loading, setLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [data, setData] = useState({
    editorCanvases: [],
    editorTemplates: [],
    giottoImages: [],
    giottoBackgrounds: [],
  });
  const [counts, setCounts] = useState({
    editorCanvases: 0,
    editorTemplates: 0,
    giottoImages: 0,
    giottoBackgrounds: 0,
  });
  const [hasMore, setHasMore] = useState({
    editorCanvases: true,
    editorTemplates: true,
    giottoImages: true,
    giottoBackgrounds: true,
  });

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  
  // Track pagination for each data type
  const skipRefs = useRef({
    editorCanvases: 0,
    editorTemplates: 0,
    giottoImages: 0,
    giottoBackgrounds: 0,
  });

  const pageSize = 20; // Items per page

  /**
   * Generic function to fetch data for any Giotto data type
   */
  const fetchGiottoDataType = useCallback(async (
    dataType, 
    query, 
    append = false
  ) => {
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
    
    try {
      if (!append) {
        setLoading(true);
      }

      const token = await getTokenSilently();
      
      // Build where clause - filter by organization if orgId is provided
      const where = orgId ? {
        org_pointer: {
          have: {
            objectId: {
              equalTo: orgId
            }
          }
        }
      } : {};
      
      const variables = {
        first: pageSize,
        skip: append ? skipRefs.current[dataType] : 0,
        where: where,
        order: ["updatedAt_DESC"]
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: query,
          variables: variables
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = response.data;
      
      // Map data types to their response field names
      const responseFieldMap = {
        editorCanvases: 'findEditorCanvas',
        editorTemplates: 'findEditorTemplates', 
        giottoImages: 'editor_images_defaults',
        giottoBackgrounds: 'findEditorBackgrounds'
      };
      
      const connectionField = responseFieldMap[dataType];
      const connectionData = responseData[connectionField];

      if (connectionData) {
        const { edges, count } = connectionData;
        const results = edges.map(edge => edge.node);

        if (append) {
          setData(prev => ({
            ...prev,
            [dataType]: [...prev[dataType], ...results]
          }));
        } else {
          setData(prev => ({
            ...prev,
            [dataType]: results
          }));
        }

        setCounts(prev => ({
          ...prev,
          [dataType]: count || 0
        }));

        // Update skip for next pagination
        skipRefs.current[dataType] = append 
          ? skipRefs.current[dataType] + results.length
          : results.length;

        // Check if there's more data
        setHasMore(prev => ({
          ...prev,
          [dataType]: (append ? skipRefs.current[dataType] : results.length) < (count || 0)
        }));
      }

      if (!append) {
        setLoading(false);
      }
    } catch (error) {
      console.error(`Error fetching ${dataType}:`, error);
      if (!append) {
        setLoading(false);
      }
    }
  }, [orgId, getTokenSilently, env, pageSize]);

  /**
   * Initial fetch for all data types
   */
  const fetchAllInitialData = useCallback(async () => {
    // Reset skip counters
    skipRefs.current = {
      editorCanvases: 0,
      editorTemplates: 0,
      giottoImages: 0,
      giottoBackgrounds: 0,
    };

    setLoading(true);

    try {
      // For global view, only fetch editor canvases
      // For org view, fetch all data types
      if (!orgId) {
        await fetchGiottoDataType('editorCanvases', editorCanvasesQuery, false);
      } else {
        await Promise.all([
          fetchGiottoDataType('editorCanvases', editorCanvasesQuery, false),
          fetchGiottoDataType('editorTemplates', editorTemplatesQuery, false),
          fetchGiottoDataType('giottoImages', editorImagesQuery, false),
          fetchGiottoDataType('giottoBackgrounds', editorBackgroundsQuery, false),
        ]);
      }
    } finally {
      setLoading(false);
      setInitialFetchDone(true);
    }
  }, [orgId, fetchGiottoDataType]);

  /**
   * Load more data for a specific type
   */
  const loadMore = useCallback((dataType) => {
    if (!hasMore[dataType] || loading) return;

    const queryMap = {
      editorCanvases: editorCanvasesQuery,
      editorTemplates: editorTemplatesQuery,
      giottoImages: editorImagesQuery,
      giottoBackgrounds: editorBackgroundsQuery,
    };

    fetchGiottoDataType(dataType, queryMap[dataType], true);
  }, [hasMore, loading, fetchGiottoDataType]);

  return {
    loading,
    initialFetchDone,
    data,
    counts,
    hasMore,
    fetchAllInitialData,
    loadMore,
  };
};